/* global __webpack_modules__ */

/* eslint id-length: [2, {"exceptions": ["a","b","t","m","x"]}] */
import mori from 'mori';
import { appDefault } from  '../config/config';
import * as maps from '../config/maps';
import * as util from '../util/util';
import jenks from '../util/jenks';
import getSelectedDistributions from './getSelectedDistributions';
import filtersModule from '../filters/filters';
import switchFilter from '../filters/switchFilter';
let filterSwitch = null;

if (__webpack_modules__[require.resolveWeak('angular')]) {
  filterSwitch = angular
    .injector(['ng', filtersModule])
    .get('filterSwitchFilter');
} else {
  //  This is react's territory
  filterSwitch = switchFilter();
}

export default function(data, metaData, options, treeContextSlug, timeframes) {
  // Get geography code map from topojson
  const geoContext = 'usa';
  const geoCodeMap = maps.getGeoCodeMap(geoContext);
  const mapConfig = mori.toJs(maps.mapDefault(geoContext));
  const isLocked = mori.getIn(data.config, ['globalRules', 'Locked'], false);
  const isGeomulti = mori.getIn(
    data.config,
    ['globalRules', 'Geomulti'],
    false
  );
  const mapBaseColumn = mori.getIn(
    data.config,
    ['globalRules', 'Map Base Column'],
    false
  );
  let baseColumn;

  // Get the current data view object by index
  const dataviewType = data.globalSettings.hasDataviews && !isLocked ?
    mori.nth(metaData.immutableDataviews, +(options.dataView || 0)) :
    false;

  // Currently selected distributions by matching slugs, or all
  const distributions = options.selectedDistributions ?
    mori.filter((x) => {
      return options.selectedDistributions === (mori.get(x, 'slug'));
    }, metaData.immutableDistributions) :
    metaData.immutableDistributions;

  // Create sets as keys for data
  const selected = getSelectedDistributions(
    data,
    metaData,
    distributions,
    dataviewType,
    isGeomulti
  );

  // Map selected headers by distribution name and type
  // Pluck distribution names from the selected distributions array
  const distributionNames = mori.set(mori.map(
    (characteristic) => mori.get(characteristic, 'characteristic'),
    distributions
  ));
  const selectedHeader = mori.first(mori.rest(mori.filter(
    (header) => {
      return mori.get(header, 'idx') === 0 ||
        mori.hasKey(distributionNames, mori.get(header, 'characteristic')) &&
        (!dataviewType ||
         dataviewType.type === mori.get(header, 'type') ||
         dataviewType.label === mori.get(header, 'label'));
    },
    metaData.immutableHeaders
  )));

  // Normally the base column is just the first
  if (!isGeomulti) {
    baseColumn = mori.vector(mori.set([
      mori.get(mori.first(metaData.immutableHeaders), 'characteristic'),
      mori.get(mori.first(metaData.immutableHeaders), 'label'),
    ]));
  // otherwise get the base column specified by config
  } else {
    baseColumn = mori.transduce(
      mori.comp(
        mori.filter(
          (header) => mori.equals(
            mori.get(header, 'characteristic'),
            mori.getIn(data.config, ['globalRules', 'Link Column'])
          )
        ),
        mori.map(
          (header) => {
            const dataviewLabel = dataviewType ?
              dataviewType.label :
              mori.get(header, 'label');
            return mori.set([
              mori.get(header, 'characteristic'),
              dataviewLabel,
            ]);
          }
        )
      ),
      mori.completing(mori.conj),
      mori.vector(),
      metaData.immutableHeaders
    );
  }

  // Make an array of selected/parent selection rows
  const parentColumns = isGeomulti ?
    mori.map(
      (row) => {
        const rowKey = mori.first(row);
        const columns = metaData.columns.nested[treeContextSlug] || {};
        const currentChild = (columns.children || {})[rowKey];
        return currentChild.label || rowKey;
      },
      mori.toClj((options.selectedRows || {})[treeContextSlug] || {})
    ) :
    false;

  // Join the base column and the selected sets
  const selectedSetKeys = mori.into(baseColumn, selected);

  // Set map constructor values
  mapConfig.scope = geoContext;
  mapConfig.data = buildMapData(
    timeframes,
    selectedSetKeys,
    geoCodeMap,
    selectedHeader,
    baseColumn,
    parentColumns,
    isGeomulti,
    mapBaseColumn
  );
  mapConfig.setProjection = maps.setMapProjection(geoContext, mapConfig);
  mapConfig.fills = {};

  // Calculate and apply fill and legend
  const { fills, labels, mapDataProcessed } = buildMapFills(
    mapConfig.fills,
    mapConfig.data
  );

  mapConfig.fills = fills;
  mapConfig.labels = labels;
  mapConfig.labels.defaultFill = mapBaseColumn ?
    'Firm is Not Operating in State' : mapConfig.labels.defaultFill;
  mapConfig.data = mapDataProcessed;

  return {
    data: mapConfig,
    plugins: buildMapPlugins(geoContext),
  };
}

function buildMapData(
  timeframes,
  selected,
  codes,
  selectedHeader,
  baseColumn,
  parentColumns,
  isGeomulti,
  mapBaseColumn) {
  const firstKey = mori.first(selected);
  const secondKey = mori.first(mori.rest(selected));

  const baseKey = isGeomulti ? secondKey : firstKey;
  const dataKey = isGeomulti ? firstKey : secondKey;

  return mori.into(mori.hashMap(), mori.map((x) => {
    // geomulti datapoints are simply the header title as the value in all
    // states that exist
    if (isGeomulti) {
      const title = mori.get(selectedHeader, 'characteristic');

      const value = mapBaseColumn ? mori.hashMap(
        codes[mori.get(x, baseKey)],
        mori.hashMap(
          mori.get(x, dataKey),
          mori.map((item) => mori.hashMap(
            mori.first(item),
            mori.get(x, item)
          ),
          mori.filter((item) =>
            mori.first(item) !== mori.first(baseKey)
              && mori.first(item) !== mori.first(dataKey),
            mori.keys(x)
          ))
        )
      ) : title;

      return mori.hashMap(
        codes[mori.get(x, baseKey)],
        mori.hashMap(
          'value', value,
          'raw', mapBaseColumn ? 'Firm is Operating in State' : title,
          'formatted', mapBaseColumn ? 'Firm is Operating in State' : title,
          'mapBaseColumn', mapBaseColumn
        )
      );
    }
    let rawNum = util.kffNumericValue(mori.get(x, dataKey));
    if (rawNum !== null && /^percent/.test(mori.get(selectedHeader, 'type'))) {
      // @TODO replace with built in filter
      rawNum = util.numberPrecision(
        rawNum,
        util.getPrecision(mori.toJs(selectedHeader)),
        true
      );
    }
    const formatted = filterSwitch(
      mori.get(x, dataKey),
      mori.toJs(selectedHeader)
    );
    return mori.hashMap(
      codes[mori.get(x, baseKey)],
      mori.hashMap(
        'value', mori.get(x, dataKey),
        'raw', rawNum !== null ? rawNum : mori.get(x, dataKey),
        'formatted', formatted
      )
    );
  }, mori.filter(
    (x) => {
      return !parentColumns ?
        // filter by actual states
        codes[mori.get(x, baseKey)] :
        // filter by selected row values
        mori.equals(
          mori.get(x, mori.first(baseColumn)),
          mori.first(parentColumns)
        );
    },
    mori.first(mori.rest(mori.first(timeframes))))
  ));
}

/**
 * Apply fill and legend calculation for datamap constructor
 */
function buildMapFills(mapFills, mapData) {
  // Calculate breaks using jenks
  const breaks = buildBreaks(mapData);

  // Build fills and legend using breaks data
  const { fills, labels } = buildLegend(breaks, mapFills);

  // Apply fills to map data
  const mapDataProcessed = annotateData(mapData, breaks);

  return { fills, labels, mapDataProcessed };
}

/**
 * Apply the correct fill keys to map data objects
 */
function annotateData(data, breaks) {
  return mori.toJs(mori.transduce(
    mori.map((x) => {
      let _x = mori.hashMap(mori.first(x), mori.first(mori.rest(x)));
      const fillKey = getFillKey(
        mori.get(breaks, 'useTextForFill') ?
          mori.get(mori.first(mori.rest(x)), 'formatted') :
          mori.get(mori.first(mori.rest(x)), 'raw'),
        breaks
      );
      _x = mori.assocIn(_x, [mori.first(x), 'fillKey'], fillKey);
      return _x;
    }),
    mori.completing(mori.conj),
    mori.hashMap(),
    data
  ));
}

/**
 * Get the fill key for given data value
 */
function getFillKey(value, breaks) {
  if (mori.some((val) => val === value, mori.get(breaks, 'text'))) {
    return appDefault.dataNotAvailable.indexOf(value) > -1 || !value ?
      'defaultFill' :
      value;
  } else if (mori.get(breaks, 'useTextForFill') &&
      mori.intoArray(mori.get(breaks, 'text')).indexOf(value) > -1
  ) {
    return mori.intoArray(mori.get(breaks, 'text')).indexOf(value);
  } else if (!isNaN(value)) {
    // If we have fewer than the max number of breaks,
    // we need to map data to the break points exactly.
    // This way, we are not grouping small data sets into
    // one break point.
    //
    // If there are more than max breaks, we need to build a
    // set of ranges.
    const breakIndex = mori.intoArray(
      mori.get(breaks, 'fewerThanMaxBreaks') ?
        mori.get(breaks, 'values') :
        mori.rest(mori.get(breaks, 'values'))
    ).reduce((acc, point, index) => {
      if (mori.get(breaks, 'fewerThanMaxBreaks')) {
        return +value === point && acc < 0 ? index : acc;
      }
      return +value < point && acc < 0 ? index : acc;
    }, -1);
    if (breakIndex === -1) {
      return mori.count(mori.get(breaks, 'values')) - 2;
    }
    return `${breakIndex}`;
  }
  return 'defaultFill';
}

/**
 * Constuct breaks arrays for numerical and text values,
 * so they can be combined.
 */
function buildBreaks(data) {
  let values = mori.groupBy(
    (value) => util.kffIsNumber(mori.get(value, 'raw')) ? 'numeric' : 'text',
    mori.map((row) => mori.first(mori.rest(row)), data)
  );

  // sort numeric
  values = mori.assocIn(values, ['numeric'], mori.sort(
    (a, b) => mori.get(a, 'raw') - mori.get(b, 'raw'),
    mori.get(values, 'numeric')
  ));

  let textBreaks = mori.distinct(
    mori.map((value) => {
      return appDefault.dataNotAvailable.indexOf(mori.get(value, 'raw')) > -1 ?
        'N/A' :
        mori.get(value, 'raw');
    }, mori.get(values, 'text')));

  let numeralBreaks = [];

  // If we have few than 5 numeric values, we need to let
  // the fillKey lookup code know that it has to look at
  // the rawValues for data, else no key would be found
  const useTextForFill = mori.count(mori.get(values, 'numeric')) < 5;

  if (useTextForFill) {
    textBreaks = mori.into(textBreaks, mori.distinct(mori.map(
      (value) => mori.get(value, 'formatted'), mori.get(values, 'numeric'))
    ));
  } else {
    const separated = mori.groupBy(
      (value) => +mori.get(value, 'raw') < 0 ? 'negative' : 'positive',
      mori.get(values, 'numeric')
    );
    const offset = mori.hasKey(separated, 'negative') ? 1 : 0;

    // Get Positive Breaks
    const positiveNumeralBreaks = mori.count(
      mori.distinct(
        mori.map(
          (value) => +mori.get(value, 'raw'), mori.get(separated, 'positive')
        )
      )
    ) < appDefault.maxBreaks ?
      mori.toJs(
        mori.distinct(
          mori.map(
            (value) => +mori.get(value, 'raw'), mori.get(separated, 'positive')
          )
        )
      ) :
      jenks(
        mori.toJs(mori.map(
          (value) => +mori.get(value, 'raw'), mori.get(separated, 'positive')
        )),
        (appDefault.maxBreaks - offset)
      ) || [];

    numeralBreaks = positiveNumeralBreaks.concat(
      offset ? jenks(
        mori.toJs(mori.map(
          (value) => +mori.get(value, 'raw'), mori.get(separated, 'negative')
        )),
        (appDefault.maxBreaks - mori.count(
          mori.distinct(
            positiveNumeralBreaks.filter((x) => x || x === 0)
          )
        ))
      ) : []
    ).sort((a, b) => a > b);
  }

  // in case the sample is too small, or doesn't have unique values
  // TODO: test if this is necessary
  const realBreaks = mori.distinct(numeralBreaks.filter((x) => x || x === 0));

  if (mori.count(realBreaks) >= 1) {
    // Find the longest decimal position, so we can pad all values
    const numeralPrecisionMax = numeralBreaks.reduce((max, curr) => {
      const stringValue = curr + '';
      if (stringValue.indexOf('.') === -1) {
        return max;
      }
      const precision = stringValue.split('.')[1].length;
      if (precision > max) {
        return precision;
      }
      return max;
    }, 0);
    // Pad
    numeralBreaks = mori.distinct(mori.map((value) => {
      return (value === 0 || typeof value === 'undefined') ?
        0 :
        +value.toFixed(numeralPrecisionMax);
    }, numeralBreaks));
  }

  return mori.hashMap(
    'text', textBreaks,
    'values', numeralBreaks,
    'rawValues', values,
    'useTextForFill', useTextForFill,
    'fewerThanMaxBreaks', mori.count(numeralBreaks) < appDefault.maxBreaks
  );
}

/**
 * Process breaks for legend and fills, combining if necessary.
 */
function buildLegend(breaks, fills) {
  const labels = {};
  // Don't include default fills
  const textBreaks = mori.filter((x) => {
    return (x && x.length &&
      appDefault.dataNotAvailable.indexOf(x) === -1);
  }, mori.get(breaks, 'text'));

  const lengthOffset = mori.get(breaks, 'fewerThanMaxBreaks') ? 0 : 1;

  let legendLength = (!mori.isEmpty(mori.get(breaks, 'values')) ?
    mori.count(mori.get(breaks, 'values')) - lengthOffset :
    0) + mori.count(textBreaks);
  let colorIndex = 0;

  if (legendLength > 6) {
    legendLength = 'full';
  }

  // Map breaks for numerical values to legend + fills
  if (!mori.isEmpty(mori.get(breaks, 'values'))) {
    // Group values by breakpoint
    const grouped = mori.toJs(mori.groupBy(
      (row) => {
        const group = mori.peek(mori.into(
          mori.vector(),
          mori.takeWhile(
            (breakValue) => breakValue <= mori.get(row, 'raw'),
            mori.get(breaks, 'values')
          )
        ));
        // The last breakpoint is a bound, not a group
        if (!mori.get(breaks, 'fewerThanMaxBreaks') &&
          group === mori.last(mori.get(breaks, 'values'))
        ) {
          return mori.count(mori.get(breaks, 'values')) - 2;
        }
        return mori.toJs(mori.get(breaks, 'values')).indexOf(group);
      },
      mori.getIn(breaks, ['rawValues', 'numeric'])
    ));

    Object.keys(grouped).forEach((idx) => {
      const startBreak = grouped[idx][0].formatted;
      const endBreak = grouped[idx][grouped[idx].length - 1].formatted;
      labels[idx] = `${startBreak}`;
      if (startBreak !== endBreak) {
        labels[idx] += `- ${endBreak}`;
      }
      fills[idx] = maps.mapColors[legendLength][colorIndex];
      colorIndex++;
    });
  }

  // Map breaks for text values to legend + fills
  if (mori.count(mori.get(breaks, 'text'))) {
    mori.each(mori.sort(
      (a, b) => a.localeCompare(b),
      mori.filter((x) => {
        return x &&
          appDefault.dataNotAvailable.indexOf(x) === -1 &&
          x.length;
      }, mori.get(breaks, 'text'))
    ), (x) => {
      labels[mori.count(mori.get(breaks, 'values')) + colorIndex] = x;
      fills[x] = maps.mapColors[legendLength][colorIndex];
      colorIndex++;
    });
  }

  // Make sure we set the defaultFill
  fills.defaultFill = maps.mapColors.defaultColor;
  labels.defaultFill = 'N/A';

  return { fills, labels };
}

/**
 * Set up plugins object for the map
 */
function buildMapPlugins(geoContext) {
  const plugins = {
    mapCustomLegend: maps.mapCustomLegend,
  };
  if (geoContext === 'usa') {
    plugins.mapCustomLabels = maps.mapCustomLabels;
  }
  return plugins;
}



// WEBPACK FOOTER //
// ../datacenter/app/js/services/mapData.js